import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import BrushIcon from '@mui/icons-material/Brush';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AnimationIcon from '@mui/icons-material/Animation';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '140px 60px',
  background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '800px',
    background: 'radial-gradient(circle, rgba(212, 175, 55, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '80px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#d4af37',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px',
  marginBottom: '20px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '18px',
  fontWeight: 300,
  color: 'rgba(255, 255, 255, 0.6)',
  maxWidth: '600px',
  margin: '0 auto',
  lineHeight: 1.7
});

const ServicesGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const ServiceCard = styled(Box)(({ index }) => ({
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '24px',
  padding: '40px',
  cursor: 'pointer',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    background: 'rgba(255, 255, 255, 0.06)',
    borderColor: 'rgba(212, 175, 55, 0.3)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 40px rgba(212, 175, 55, 0.1)',
    '&::before': {
      opacity: 1
    },
    '& .service-icon': {
      background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
      boxShadow: '0 8px 30px rgba(212, 175, 55, 0.4)',
      '& svg': {
        color: '#1a1a2e'
      }
    },
    '& .service-arrow': {
      opacity: 1,
      transform: 'translateX(0)'
    },
    '& .card-number': {
      color: '#d4af37'
    }
  },
  '@media (max-width: 768px)': {
    padding: '32px'
  }
}));

const CardNumber = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.2)',
  marginBottom: '24px',
  transition: 'color 0.3s ease'
});

const IconWrapper = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '28px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '& svg': {
    fontSize: '28px',
    color: '#d4af37',
    transition: 'color 0.3s ease'
  }
});

const ServiceTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '22px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '16px',
  letterSpacing: '-0.3px'
});

const ServiceDescription = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.8,
  marginBottom: '28px'
});

const CardFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: '20px',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)'
});

const PriceText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: '#d4af37'
});

const LearnMore = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& span': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.8)'
  }
});

const ArrowIcon = styled(ArrowForwardIcon)({
  fontSize: '16px',
  color: '#d4af37',
  opacity: 0,
  transform: 'translateX(-10px)',
  transition: 'all 0.3s ease'
});

const iconMap = {
  Strategy: DesignServicesIcon,
  Design: BrushIcon,
  Development: CodeIcon,
  Marketing: CampaignIcon,
  Motion: AnimationIcon,
  Photography: PhotoCameraIcon,
  Default: DesignServicesIcon
};

const ServicesSection = ({ services, products }) => {
  const items = services || products || [];
  if (items.length === 0) return null;

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || iconMap.Default;
    return <IconComponent />;
  };

  return (
    <SectionContainer id="services">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <BadgeText>Our Services</BadgeText>
          </SectionBadge>
          <SectionTitle>
            What We <GradientText>Offer</GradientText>
          </SectionTitle>
          <SectionSubtitle>
            Premium solutions tailored to elevate your business and exceed expectations
          </SectionSubtitle>
        </SectionHeader>

        <ServicesGrid>
          {items.slice(0, 6).map((item, index) => (
            <ServiceCard key={item.id || index} index={index}>
              <CardNumber className="card-number">
                {String(index + 1).padStart(2, '0')}
              </CardNumber>

              <IconWrapper className="service-icon">
                {getIcon(item.icon)}
              </IconWrapper>

              <ServiceTitle>{item.name || item.title}</ServiceTitle>

              <ServiceDescription>
                {item.description?.substring(0, 120)}
                {item.description?.length > 120 ? '...' : ''}
              </ServiceDescription>

              <CardFooter>
                <PriceText>{item.price || 'Contact us'}</PriceText>
                <LearnMore>
                  <span>Learn More</span>
                  <ArrowIcon className="service-arrow" />
                </LearnMore>
              </CardFooter>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ServicesSection;
